import React, { useState } from 'react';
import {
    Card,
    Text,
    Input,
    Button,
    Field,
    Spinner,
    MessageBar,
    MessageBarBody,
} from '@fluentui/react-components';
import { authService, LoginCredentials } from '../../services/auth/authService';

interface LoginProps {
    onLoginSuccess: () => void;
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
    const [credentials, setCredentials] = useState<LoginCredentials>({
        email: '',
        password: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleInputChange = (field: keyof LoginCredentials, value: string) => {
        setCredentials((prev) => ({
            ...prev,
            [field]: value,
        }));
        // Clear error when user starts typing
        if (error) setError(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const response = await authService.login(credentials);

            if (response.success) {
                console.log('✅ Login successful:', response.user);
                onLoginSuccess();
            } else {
                setError(response.message || 'Login failed');
            }
        } catch (error: any) {
            console.error('❌ Login error:', error);
            setError(error.message || 'An error occurred during login');
        } finally {
            setIsLoading(false);
        }
    };

    const fillDemoCredentials = (role: 'doctor' | 'support') => {
        if (role === 'doctor') {
            setCredentials({
                email: 'sarah.johnson@baylor.edu',
                password: 'password123',
            });
        } else {
            setCredentials({
                email: 'support@baylor.edu',
                password: 'password123',
            });
        }
    };

    return (
        <div
            style={{
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
            }}
        >
            <Card
                style={{
                    width: '400px',
                    padding: '32px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                }}
            >
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <Text size={800} weight="bold" style={{ color: '#323130' }}>
                        Baylor Psychiatry
                    </Text>
                    <Text
                        size={400}
                        style={{ display: 'block', color: '#605e5c', marginTop: '8px' }}
                    >
                        AI-Powered Teams Add-on
                    </Text>
                </div>

                {error && (
                    <MessageBar intent="error" style={{ marginBottom: '16px' }}>
                        <MessageBarBody>{error}</MessageBarBody>
                    </MessageBar>
                )}

                <form onSubmit={handleSubmit}>
                    <Field label="Email" required>
                        <Input
                            type="email"
                            value={credentials.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="Enter your email"
                            disabled={isLoading}
                            required
                        />
                    </Field>

                    <Field label="Password" required style={{ marginTop: '16px' }}>
                        <Input
                            type="password"
                            value={credentials.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            placeholder="Enter your password"
                            disabled={isLoading}
                            required
                        />
                    </Field>

                    <Button
                        type="submit"
                        appearance="primary"
                        disabled={isLoading || !credentials.email || !credentials.password}
                        style={{
                            width: '100%',
                            marginTop: '24px',
                            minHeight: '40px',
                        }}
                    >
                        {isLoading ? (
                            <>
                                <Spinner size="tiny" style={{ marginRight: '8px' }} />
                                Signing in...
                            </>
                        ) : (
                            'Sign In'
                        )}
                    </Button>
                </form>

                {/* Demo credentials buttons */}
                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                    <Text
                        size={300}
                        style={{ color: '#605e5c', marginBottom: '12px', display: 'block' }}
                    >
                        Demo Credentials:
                    </Text>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <Button
                            size="small"
                            appearance="outline"
                            onClick={() => fillDemoCredentials('doctor')}
                            disabled={isLoading}
                        >
                            👨‍⚕️ Doctor
                        </Button>
                        <Button
                            size="small"
                            appearance="outline"
                            onClick={() => fillDemoCredentials('support')}
                            disabled={isLoading}
                        >
                            👩‍💼 Support Staff
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
};
